<script lang="ts">
  import { onMount } from 'svelte';
  import { Router, Route } from 'svelte-routing';
  import { DEFAULT_MODE } from '@atlasguess/games';
  import Demo from './routes/Demo.svelte';
  import Play from './routes/Play.svelte';
  import Login from './routes/auth/Login.svelte';
  import Register from './routes/auth/Register.svelte';
  import RegisterFinish from './routes/auth/RegisterFinish.svelte';
  import VerifyPending from './routes/auth/VerifyPending.svelte';
  import Account from './routes/account/Index.svelte';
  import DeleteConfirm from './routes/account/DeleteConfirm.svelte';
  import PublicProfile from './routes/profile/Public.svelte';
  import MarkdownPage from './routes/static/MarkdownPage.svelte';
  import Donate from './routes/donate/Donate.svelte';
  import DonateThanks from './routes/donate/Thanks.svelte';
  import Leaderboards from './routes/leaderboards/Index.svelte';
  import AppLayout from './components/AppLayout.svelte';
  import PageShell from './components/PageShell.svelte';
  import RequireAuth from './components/RequireAuth.svelte';
  import RequireGuest from './components/RequireGuest.svelte';
  import VerifyEmailBanner from './components/VerifyEmailBanner.svelte';
  import WelcomeNotice from './components/WelcomeNotice.svelte';
  import Navigate from './components/Navigate.svelte';
  import { bootAuth } from './lib/stores/auth';
  import { fromQuery } from './lib/stores/toast';

  // Compute the home-route's scope/format from DEFAULT_MODE so changing
  // the default in one place (the registry) updates both the rendered
  // mode and the sidebar's active-state matching.
  const HOME_SCOPE = `${DEFAULT_MODE.scope.entity}-${DEFAULT_MODE.scope.region}`;
  const HOME_FORMAT = DEFAULT_MODE.format;

  // Fire-and-forget — bootAuth resolves authReady regardless of outcome so
  // route guards never hang. The game routes don't gate on auth today, so
  // letting this run in the background keeps first paint fast.
  //
  // Status / error query-param toasts ALSO fire here at the root so they
  // work for any landing route — server redirects like
  // /account?status=restored land in AppLayout (not PageShell, which had
  // its own fromQuery handler), and we want the welcome-back toast to
  // surface regardless of which shell the user lands in.
  onMount(() => {
    void bootAuth();

    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (url.searchParams.has('status') || url.searchParams.has('error')) {
      const cleaned = fromQuery(url);
      window.history.replaceState({}, '', cleaned.toString());
    }
  });
</script>

<!-- Flat routing — each route owns its own AppLayout. The previous nested-
     wildcard pattern caused mount/unmount instability that surfaced as an
     OOM crash; per-route ownership eliminates the ambiguity. Engine
     persistence across navigation is deferred until we have profiled
     evidence the re-init time matters. -->
<!-- Sticky verify-email bar lives outside the Router so it persists across
     navigation. Both shells honour var(--verify-banner-h) for their top
     inset, set by the banner when visible. -->
<VerifyEmailBanner />

<!-- One-time welcome modal — explains the daily-cities concept to
     new visitors. Shown once per browser via a versioned localStorage
     key. To DISABLE: comment out / remove this line. To replace with
     a fresh popup later: bump NOTICE_VERSION inside the component. -->
<WelcomeNotice />

<Router>
  <Route path="/demo"><Demo /></Route>

  <!-- Default route lands on DEFAULT_MODE — most prominent mode, returns
       the most engaged player by giving them the day's puzzle. Practice
       is one click away in the sidebar. -->
  <Route path="/">
    <AppLayout>
      <Play scope={HOME_SCOPE} format={HOME_FORMAT} />
    </AppLayout>
  </Route>

  <!-- Legacy redirect: the DEFAULT_MODE used to live at /play/{scope}/daily;
       now it's only at `/` so the share URL is the bare site origin. Keep
       this redirect for external links / bookmarks pointing at the old shape.
       Declared before the wildcard so it short-circuits the regex match. -->
  <Route path={`/play/${HOME_SCOPE}/${HOME_FORMAT}`}>
    <Navigate to="/" replace />
  </Route>

  <Route path="/play/:scope/:format" let:params>
    <AppLayout>
      <Play scope={params.scope} format={params.format} />
    </AppLayout>
  </Route>

  <!-- Optional 4th URL segment narrows the active pool to a continent or
       a single country (`/play/cities-world/practice/europe`,
       `/play/countries-world/practice/cote-d-ivoire`). The mode component
       composes the slug-derived fragment on top of its base filter so
       region-quiz modes can also be sub-filtered without spawning new
       mode configs. -->
  <Route path="/play/:scope/:format/:filter" let:params>
    <AppLayout>
      <Play scope={params.scope} format={params.format} filter={params.filter} />
    </AppLayout>
  </Route>

  <!-- Auth + account routes wrap their content in PageShell, parallel to
       how game routes wrap in AppLayout. Guest-only routes wrap in
       RequireGuest so a logged-in user can't see them; auth-required
       routes will use RequireAuth (added with /account in step 6). -->
  <Route path="/login">
    <PageShell narrow>
      <RequireGuest>
        <Login />
      </RequireGuest>
    </PageShell>
  </Route>

  <Route path="/register">
    <PageShell narrow>
      <RequireGuest>
        <Register />
      </RequireGuest>
    </PageShell>
  </Route>

  <!-- OAuth callback lands here; the route requires the OAuth pending
       session, not an authenticated user, so RequireGuest is correct. -->
  <Route path="/register/finish">
    <PageShell narrow>
      <RequireGuest>
        <RegisterFinish />
      </RequireGuest>
    </PageShell>
  </Route>

  <!-- Verify-pending is only meaningful for a signed-in but unverified
       user. RequireAuth ensures a stranger can't see their email. -->
  <Route path="/verify-pending">
    <PageShell narrow>
      <RequireAuth>
        <VerifyPending />
      </RequireAuth>
    </PageShell>
  </Route>

  <!-- Account is full-width (not narrow) so the tab nav can breathe and
       facts grids can lay out across columns on wider screens. -->
  <Route path="/account">
    <PageShell>
      <RequireAuth>
        <Account />
      </RequireAuth>
    </PageShell>
  </Route>

  <Route path="/account/delete">
    <PageShell narrow>
      <RequireAuth>
        <DeleteConfirm />
      </RequireAuth>
    </PageShell>
  </Route>

  <!-- Public profile is unauthenticated; the route component handles
       case-folding the username and 404 surfaces. Default-width shell
       so the profile card lines up with the TopNav + LegalFooter
       (both max-width: 1100 px) instead of capping at narrow's 640 px. -->
  <Route path="/u/:username" let:params>
    <PageShell>
      <PublicProfile username={params.username} />
    </PageShell>
  </Route>

  <!-- Static legal/info pages. MarkdownPage owns its own h1 and
       document.title (rendered from the markdown source's leading H1)
       so PageShell stays unaware of the slug. -->
  <!-- Markdown long-form pages render at the full PageShell width
       (1100px) so the body matches the TopNav + LegalFooter chrome
       above and below it. The serif body type is sized for that
       measure (see styles/pages/static.scss). -->
  <Route path="/privacy">
    <PageShell>
      <MarkdownPage slug="privacy" />
    </PageShell>
  </Route>

  <Route path="/terms">
    <PageShell>
      <MarkdownPage slug="terms" />
    </PageShell>
  </Route>

  <Route path="/contact">
    <PageShell>
      <MarkdownPage slug="contact" />
    </PageShell>
  </Route>

  <!-- /donate is the Stripe Checkout entry point; /donate/thanks is the
       post-checkout landing that refreshes auth so the donor badge picks
       up the new tier without a manual reload. -->
  <Route path="/donate">
    <PageShell narrow>
      <Donate />
    </PageShell>
  </Route>

  <Route path="/donate/thanks">
    <PageShell narrow>
      <DonateThanks />
    </PageShell>
  </Route>

  <!-- Public daily leaderboard. Hero card matches the Stats hero;
       URL is singular ("/leaderboard") even though the page lists
       multiple players — read-aloud reads cleaner ("the leaderboard")
       and matches Twitter's /home, GitHub's /pulls-singular convention
       for top-level dashboards. Hard-coded to cities-world-daily for
       v1 (per phase-e-plan §2 — only ranked surface that ships at
       launch). -->
  <Route path="/leaderboard">
    <PageShell>
      <Leaderboards />
    </PageShell>
  </Route>
</Router>
